// The offers a customer can use at checkout: codes, coins and the online
// saving. Codes live in Firestore (Coupons/{CODE}), made and changed from the
// admin panel's Coupons page; the rules below decide whether one applies, and
// the same file runs in three places:
//   - the website (checkout, the account's Coupons tab),
//   - the admin panel (a copy at admin/src/Data/offers.js),
//   - the server, api/place-order.js, through api/_lib/offers.cjs.
// After a change run `node scripts/check-taxonomy-sync.js --write` and copy
// this file to the admin panel. No imports: the server build needs a plain
// module.

// Used only while the Coupons collection is empty (before the admin panel
// has saved any): the offers agreed on 2026-10-27.
export const COUPONS = {
    MAVIINCI500: { code: 'MAVIINCI500', type: 'flat', value: 500, minOrder: 9500, active: true },
    MAVIINCI100: { code: 'MAVIINCI100', type: 'flat', value: 100, minOrder: 4500, active: true },
};
export const COUPONS_COLLECTION = 'Coupons';
export const CODE_RE = /^[A-Z0-9]{3,20}$/;

// Paying online takes this share off what is left to pay (1/20 = 5%).
export const PREPAID_SHARE = 1 / 20;

// Coins: an account starts with none. Each order earns a tenth of its value
// in coins (₹5,000 earns 500), credited by the server once the payment is
// confirmed. 10 coins take ₹1 off a later order.
export const DEFAULT_COINS = 0;
export const COINS_PER_RUPEE = 10;
export const COINS_EARNED_PER_RUPEE = 0.1;
export const coinsEarnedFor = (amount) => Math.max(0, Math.floor((Number(amount) || 0) * COINS_EARNED_PER_RUPEE));

// Where an account's wallet lives: Coins/{email or phone}, written only by
// the server. It holds the coin balance and how often each code was used.
export const COINS_COLLECTION = 'Coins';

const toMs = (v) => {
    if (!v) return 0;
    if (typeof v === 'number') return v;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
};
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const dayText = (ms) => { const d = new Date(ms); return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`; };

// A coupon as every part of the site reads it, from a Firestore document or
// the built-in list. Unknown or missing fields get safe values.
export const normalizeCoupon = (raw, id) => {
    const c = raw || {};
    const code = String(c.code || id || '').trim().toUpperCase();
    const type = c.type === 'percent' ? 'percent' : 'flat';
    const num = (v) => Math.max(0, Number(v) || 0);
    return {
        code,
        type,
        value: num(c.value),
        maxDiscount: type === 'percent' ? num(c.maxDiscount) : 0,
        minOrder: num(c.minOrder),
        active: c.active !== false,
        startsAt: toMs(c.startsAt),
        endsAt: toMs(c.endsAt),
        firstOrderOnly: !!c.firstOrderOnly,
        perCustomer: Math.floor(num(c.perCustomer)),   // 0 = no limit
        totalLimit: Math.floor(num(c.totalLimit)),     // 0 = no limit
        usedCount: Math.floor(num(c.usedCount)),
        headline: c.headline || '',
        note: c.note || '',
    };
};

export const builtInCoupons = () => Object.values(COUPONS).map((c) => normalizeCoupon(c));

// Where a coupon stands today, before any bag: running, scheduled, ended,
// used up, or switched off.
export const couponState = (c, now = Date.now()) => {
    if (!c.active) return 'off';
    if (c.startsAt && now < c.startsAt) return 'scheduled';
    if (c.endsAt && now > c.endsAt) return 'ended';
    if (c.totalLimit && c.usedCount >= c.totalLimit) return 'used_up';
    return 'running';
};

// "₹500 off" / "10% off", and the line of terms under it.
export const couponHeadline = (c) => c.headline || (c.type === 'percent' ? `${c.value}% off` : `${inr(c.value)} off`);
export const couponTerms = (c) => {
    if (c.note) return c.note;
    const parts = [];
    if (c.type === 'percent' && c.maxDiscount) parts.push(`Up to ${inr(c.maxDiscount)} off`);
    parts.push(c.minOrder ? `on orders of ${inr(c.minOrder)} or more` : 'on any order');
    if (c.firstOrderOnly) parts.push('your first order only');
    if (c.perCustomer === 1) parts.push('once per customer');
    else if (c.perCustomer > 1) parts.push(`${c.perCustomer} times per customer`);
    if (c.endsAt) parts.push(`until ${dayText(c.endsAt)}`);
    const text = parts.join(', ');
    return text.charAt(0).toUpperCase() + text.slice(1) + '.';
};

// What a code takes off an amount (0 below its minimum).
export const couponSaving = (c, amount) => {
    if (!c || amount < (Number(c.minOrder) || 0)) return 0;
    const raw = c.type === 'percent' ? (amount * c.value) / 100 : Number(c.value) || 0;
    return Math.min(c.maxDiscount ? Math.min(raw, c.maxDiscount) : raw, amount);
};

// Why a code cannot be used on this bag by this customer, in words for them;
// '' when it can. ctx: { amount, now, hasAccount, priorOrders, usesByCustomer }.
export const couponProblem = (c, ctx = {}) => {
    if (!c || !c.code) return 'This code does not exist.';
    const now = ctx.now || Date.now();
    const state = couponState(c, now);
    if (state === 'off') return 'This code is not active.';
    if (state === 'scheduled') return `This code starts on ${dayText(c.startsAt)}.`;
    if (state === 'ended') return 'This code has ended.';
    if (state === 'used_up') return 'This code has been fully used.';
    const amount = Number(ctx.amount) || 0;
    if (c.minOrder && amount < c.minOrder) return `Add ${inr(c.minOrder - amount)} more to use ${c.code} (minimum ${inr(c.minOrder)}).`;
    if ((c.firstOrderOnly || c.perCustomer) && !ctx.hasAccount) return `Sign in to use ${c.code}.`;
    if (c.firstOrderOnly && ctx.priorOrders > 0) return `${c.code} is for a first order only.`;
    if (c.perCustomer && (Number(ctx.usesByCustomer) || 0) >= c.perCustomer) return `You have already used ${c.code}${c.perCustomer > 1 ? ` ${c.perCustomer} times` : ''}.`;
    return '';
};

// What a bag comes to after each item's own discount, the amount every code
// is checked against. Same sum as the checkout's summary (cost_details.js):
// custom pieces carry a rupee discount, Studio pieces a percent, and fabric
// samples are 100 each (250 for two or more).
export const bagAfterItemDiscounts = (items = []) => {
    let total = 0;
    let samples = 0;
    (Array.isArray(items) ? items : []).forEach((it) => {
        if (!it) return;
        const isPreset = Object.prototype.hasOwnProperty.call(it, 'item_type');
        if (isPreset && it.item_type === 0) { samples += Array.isArray(it.items) ? it.items.length : 0; return; }
        const price = Number(it.price) || 0;
        const discount = Number(it.discount) || 0;
        total += isPreset ? price - (price * discount) / 100 : price - discount;
    });
    return Math.max(0, total + (samples > 1 ? 250 : samples * 100));
};

// Everything that comes off an amount, in checkout's order: the best code the
// customer can use, then coins on what is left, then the online saving.
export const savingsOn = (amount, coins = DEFAULT_COINS, coupons = builtInCoupons(), ctx = {}) => {
    const best = coupons
        .filter((c) => !couponProblem(c, { ...ctx, amount }))
        .map((c) => ({ coupon: c, value: couponSaving(c, amount) }))
        .sort((a, b) => b.value - a.value)[0];
    const coupon = best && best.value > 0 ? best : null;
    const afterCoupon = amount - (coupon ? coupon.value : 0);
    const coinValue = Math.min(Math.floor(coins) / COINS_PER_RUPEE, Math.max(0, afterCoupon));
    const afterCoins = afterCoupon - coinValue;
    const online = afterCoins * PREPAID_SHARE;
    const pay = afterCoins - online;
    return { amount, coupon, coinValue, online, pay, saved: amount - pay };
};

// ── Business accounts ──
// A boutique, showroom or tailor shows our designs to its own client,
// measures them and orders from us (docs/business-plan.md). Once the admin
// panel verifies it (Businesses/{key}, status 'verified') every order takes
// BUSINESS_PCT off the bag after item discounts, and nothing else: no codes,
// no coins (spent or earned), no prepaid saving, no cash on delivery.
// GSTIN is optional.
// The client sees our website price, the same as on the website, plus the
// boutique's service charge (SERVICE_PCT, the same for every boutique). On a
// ₹12,000 design the client pays ₹13,800, the boutique pays us ₹10,200 and
// keeps ₹3,600.
export const BUSINESSES_COLLECTION = 'Businesses';
export const BUSINESS_TYPES = ['Boutique', 'Tailor or designer', 'Wedding planner', 'Company or school (uniforms)', 'Reseller', 'Other'];
export const BUSINESS_ORDERS = ['Outfits for clients', 'Uniforms', 'Both'];
export const BUSINESS_PCT = 15;
export const SERVICE_PCT = 15;

export const isVerifiedBusiness = (biz) => !!(biz && biz.status === 'verified');
export const businessPct = (biz) => (isVerifiedBusiness(biz) ? BUSINESS_PCT : 0);
export const businessSaving = (biz, amount) => Math.round(((Number(amount) || 0) * businessPct(biz)) / 100);
// The boutique's service charge on one of our prices, and what its client pays.
export const serviceCharge = (amount) => Math.round(((Number(amount) || 0) * SERVICE_PCT) / 100);
export const clientTotal = (amount) => Math.round(Number(amount) || 0) + serviceCharge(amount);

// The server's look at what checkout claimed: was the code usable for this
// customer on this bag, did it take off what the code gives, and how many of
// the claimed coins the account really had. `couponDef` is the code as the
// server found it (normalizeCoupon), or null when the code is unknown.
// Never throws.
export const checkOffers = ({ subtotal, coupon, couponDef, couponDiscount, coinsUsed, balance, ctx = {}, business = null, businessDiscount = 0, prepaidDiscount = 0 }) => {
    const issues = [];
    // A verified business takes its discount and nothing else.
    const allowedBusiness = businessSaving(business, Number(subtotal) || 0);
    if (Math.round(Number(businessDiscount) || 0) > allowedBusiness) issues.push(`business discount ${Math.round(businessDiscount)}, allowed ${allowedBusiness}`);
    if (isVerifiedBusiness(business)) {
        if (coupon) issues.push(`business order used code ${String(coupon).toUpperCase()}`);
        if (Number(coinsUsed) > 0) issues.push(`business order used ${Math.floor(Number(coinsUsed))} coins`);
        if (Number(prepaidDiscount) > 0) issues.push(`business order took the prepaid saving (${Math.round(Number(prepaidDiscount))})`);
        return { ok: issues.length === 0, issues, allowedDiscount: 0, allowedBusiness, coinsSpent: 0, couponUsed: '' };
    }
    const amount = (Number(subtotal) || 0) - allowedBusiness;
    const code = coupon ? String(coupon).toUpperCase() : '';
    let allowedDiscount = 0;
    if (code) {
        if (!couponDef) issues.push(`unknown code ${code}`);
        else {
            const problem = couponProblem(couponDef, { ...ctx, amount });
            if (problem) issues.push(`${code}: ${problem}`);
            else {
                allowedDiscount = couponSaving(couponDef, amount);
                if (Math.round(Number(couponDiscount) || 0) > Math.round(allowedDiscount)) issues.push(`${code} took off ${Math.round(couponDiscount)}, allowed ${Math.round(allowedDiscount)}`);
            }
        }
    }
    const claimedCoins = Math.max(0, Math.floor(Number(coinsUsed) || 0));
    const have = Math.max(0, Math.floor(Number(balance) || 0));
    const coinsSpent = Math.min(claimedCoins, have);
    if (claimedCoins > have) issues.push(`used ${claimedCoins} coins, account had ${have}`);
    return { ok: issues.length === 0, issues, allowedDiscount, allowedBusiness, coinsSpent, couponUsed: code && couponDef && !issues.some((i) => i.startsWith(code)) ? code : '' };
};
